import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApi } from "../Api/ApiContext";
import { useAsyncFn } from "react-use";
import {
  Col,
  Form,
  FormText,
  InputGroup,
  InputGroupText,
  Row,
  Alert,
  Button,
  FormGroup,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { BoostrapIcon } from "./SignUpForm";
import Input from "../component/Input";

const ResetPasswordForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { storeTokenInSession, changePassword } = useApi();
  const [{ loading, error, value: response }, storeToken] = useAsyncFn(
    (token) => storeTokenInSession(token),
    []
  );

  const [
    {
      loading: changePasswordLoading,
      error: changePasswordError,
      value: changePasswordResponse,
    },
    changePasswordRequest,
  ] = useAsyncFn((password) => changePassword(password));

  const {
    register,
    handleSubmit,
    values,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: location.state && location.state.email ? location.state.email : "",
    },
  });
  const [showPassword, setShowPassword] = React.useState(false);

  const token = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (!token) {
      navigate("/auth/check-email", {
        state: { backPath: "/login" },
      });
      return;
    }
    storeToken(token);
  }, [token]);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = (data) => {
    changePasswordRequest(data.password);
  };

  if (loading || changePasswordLoading) {
    return (
      <Row>
        <Col lg={6} className="mx-auto">
          <Spinner color={"danger"} />
        </Col>
      </Row>
    );
  }

  if (
    !changePasswordLoading &&
    !changePasswordError &&
    changePasswordResponse
  ) {
    navigate("/auth/reset-password-success");
  }

  return (
    <div className={"my-3 my-lg-3"}>
      <Row>
        <Col lg={6} className="mx-auto">
          <h1 className="fs-4">Réinitialiser votre mot de passe</h1>
        </Col>
      </Row>
      {error && (
        <Row>
          <Col lg={6} className="mx-auto">
            <Alert color="danger">
              Une erreur technique est survenue. Veuillez réessayer plus tard.
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col lg={6} className="mx-auto">
          <Form onSubmit={handleSubmit(onSubmit)}>
            {values?.email && (
              <FormGroup>
                <label htmlFor={"email"} className={"form-label"}>
                  Adresse email
                </label>
                <Input
                  className={
                    errors.email ? "is-invalid form-control" : "form-control"
                  }
                  id={"email"}
                  type={"text"}
                  name={"email"}
                  value={values?.email}
                  {...register("email", {
                    required: "Ce champ est obligatoire.",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message:
                        "Le format de l'adresse e-mail n'est pas valide.",
                    },
                  })}
                  disabled
                />
              </FormGroup>
            )}
            <FormGroup>
              <label htmlFor="password" className={"form-label"}>
                Nouveau mot de passe
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.password ? "is-invalid form-control" : "form-control"
                  }
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={values?.password}
                  {...register("password", {
                    required: "Ce champ est obligatoire.",
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message: "Le format de mot de passe n'est pas valide",
                    },
                  })}
                />
                <InputGroupText>
                  {showPassword ? (
                    <BoostrapIcon
                      className="fa-solid fa-eye fs-4"
                      onClick={handleShowPassword}
                    />
                  ) : (
                    <BoostrapIcon
                      className="fa-solid fa-eye-slash fs-4"
                      onClick={handleShowPassword}
                    />
                  )}
                </InputGroupText>
              </InputGroup>
              {errors.password && (
                <div>
                  <small className={"text-danger"}>
                    {errors.password.message}
                  </small>
                </div>
              )}
              <FormText>
                Le mot de passe doit contenir au moins 8 caractères dont une
                lettre majuscule, une lettre minuscule, un chiffre et un
                caractère spécial parmi ceux-ci{" "}
                <span className={"fw-bold"}>@ $ ! % * ? &</span>
              </FormText>
            </FormGroup>

            <FormGroup>
              <label htmlFor="password-confirmation" className={"form-label"}>
                Confirmer le mot de passe
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.passwordConfirmation
                      ? "is-invalid form-control"
                      : "form-control"
                  }
                  type={showPassword ? "text" : "password"}
                  id="password-confirmation"
                  name={"passwordConfirmation"}
                  {...register("passwordConfirmation", {
                    validate: (value, formValues) =>
                      value === formValues.password,
                  })}
                />
                <InputGroupText>
                  {showPassword ? (
                    <BoostrapIcon
                      className="fa-solid fa-eye fs-4"
                      onClick={handleShowPassword}
                    />
                  ) : (
                    <BoostrapIcon
                      className="fa-solid fa-eye-slash fs-4"
                      onClick={handleShowPassword}
                    />
                  )}
                </InputGroupText>
              </InputGroup>
              {errors.passwordConfirmation && (
                <div>
                  <small className={"text-danger"}>
                    Les deux mots de passe doivent être identiques.
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup
              className={"justify-content-sm-end justify-content-around"}
              row
            >
              <Col xs={12} sm={3}>
                <Button className={"w-100"} color={"primary"} type={"submit"}>
                  Enregistrer
                </Button>
              </Col>
            </FormGroup>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPasswordForm;
